import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/apiClient';
import { FileText, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { useTenantStore } from '@/stores/useTenantStore';

export const MyDocuments: React.FC = () => {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [agreementLoading, setAgreementLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { agreementAccepted, fetchDashboardData } = useTenantStore();

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/tenants/my-documents');
            setDocuments(response.data || []);
        } catch (err: any) {
            console.error('Error fetching documents:', err);
            // Non-breaking fallback 
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptAgreement = async () => {
        setAgreementLoading(true);
        setError(null);
        try {
            await apiClient.post('/tenants/accept-agreement');
            await fetchDashboardData(); // Refresh global store state
            navigate('/tenant');
        } catch (err: any) {
            console.error('Error accepting agreement:', err);
            setError(err.response?.data?.error || 'Failed to accept agreement. Please try again.');
        } finally {
            setAgreementLoading(false);
        }
    };

    if (!agreementAccepted) {
        return (
            <div className="max-w-3xl mx-auto space-y-6">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-8 shadow-sm border border-light-border dark:border-dark-border">
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text mb-6">Lease Agreement</h1>
                    
                    {error && (
                        <div className="p-4 mb-6 bg-red-50 text-red-600 rounded-lg flex items-center gap-3">
                            <AlertCircle size={20} />
                            <p>{error}</p>
                        </div>
                    )}
                    
                    <div className="bg-light-bg dark:bg-dark-bg p-6 rounded-lg border border-light-border dark:border-dark-border h-96 overflow-y-auto mb-6 text-sm text-light-subtle dark:text-dark-subtle leading-relaxed">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Terms and Conditions</h3>
                        <p className="mb-4">By clicking "Accept", you acknowledge and agree to the following terms of your tenancy with Majengo properties.</p>
                        <ul className="list-disc pl-5 space-y-2 mb-4">
                            <li>Rent must be paid on or before the agreed due date each month.</li>
                            <li>The tenant is responsible for maintaining the property in good condition.</li>
                            <li>Any structural modifications require prior written consent from the landlord.</li>
                            <li>Subletting the property is strictly prohibited without explicit permission.</li>
                            <li>The tenant agrees to abide by all community rules and regulations.</li>
                            <li>The landlord reserves the right to inspect the property with reasonable prior notice.</li>
                        </ul>
                        <p>This is a legally binding agreement. Please ensure you have read and understood all terms before proceeding.</p>
                    </div>

                    <button 
                        onClick={handleAcceptAgreement} 
                        disabled={agreementLoading}
                        className="btn-primary w-full flex justify-center items-center gap-2"
                    >
                        {agreementLoading ? (
                            'Processing...'
                        ) : (
                            <>
                                <CheckCircle size={20} />
                                I Accept the Agreement
                            </>
                        )}
                    </button>
                    <p className="text-xs text-center text-light-subtle dark:text-dark-subtle mt-4">
                        Accepting this agreement is required to access your tenant dashboard.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">My Documents</h1>
            </div>

            <div className="bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border p-6">
                {loading ? (
                    <p className="text-center text-light-subtle dark:text-dark-subtle py-8">Loading documents...</p>
                ) : documents.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                        <h3 className="text-lg font-medium text-light-text dark:text-dark-text mb-2">No documents yet</h3>
                        <p className="text-light-subtle dark:text-dark-subtle">Your lease agreements and receipts will appear here.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {documents.map((doc, i) => (
                            <div key={i} className="flex items-center justify-between p-4 bg-light-bg dark:bg-dark-bg rounded-lg border border-light-border dark:border-dark-border group hover:border-primary transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-lg">
                                        <FileText size={24} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-light-text dark:text-dark-text">{doc.title}</h4>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                            {doc.type} • {new Date(doc.createdAt || doc.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <button className="p-2 text-light-subtle dark:text-dark-subtle hover:text-primary transition-colors hover:bg-light-card dark:hover:bg-dark-card rounded-md">
                                    <Download size={20} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
